import React, { FC, Fragment, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Switch,
    TableSortLabel,
    Typography,
} from '@mui/material';
import { OrderBy, OrderDirection } from 'models/search-result';
import { useTranslation } from 'react-i18next';
import { Organization } from 'models/organization';
import FreeTextFilterComponent from 'components/users/shared/free-text-filter/free-text-filter.component';
import FormattedAddress from 'components/shared/address/formatted-address.component';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

enum CustomerType {
    ALL = 'ALL',
    CLINIC = 'CLINIC',
    LAB = 'LAB',
}

type CustomerListProps = {
    onSelectCustomer: (customerId: string, customerType?: string) => void;
    onActivateCustomer?: (customerId: string) => void;
    showTypeFilter?: boolean;
    customers: (Partial<Organization> & { isAlreadyConnected: boolean; connectionIsActive?: boolean | null })[];
    addButtonTitle: string;
};

const CustomerList: FC<CustomerListProps> = ({
    onSelectCustomer,
    onActivateCustomer,
    customers,
    showTypeFilter,
    addButtonTitle,
}: CustomerListProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'invoices', 'client-directory']);
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'name',
        orderDirection: OrderDirection.asc,
    });
    const [search, setSearch] = useState('');
    const [customerType, setCustomerType] = useState<CustomerType>(CustomerType.ALL);
    const [onlyNotConnected, setOnlyNotConnected] = useState<boolean>(false);
    const currentOrg = currentOrgVar();

    const filteredData = useMemo(() => {
        let data = customers.filter((customer) => {
            const lowerSearch = search.toLowerCase();
            const matchesSearch = [
                customer.name,
                customer.ptjCustomerNumber,
                customer.invoicing?.costCenter,
                customer.contact?.address?.street,
                customer.contact?.address?.postalCode,
                customer.contact?.address?.city,
            ].some((field) => field?.toLowerCase().includes(lowerSearch));

            const matchesType =
                customerType && customerType !== CustomerType.ALL ? customer.type === customerType.toLowerCase() : true;

            const matchesConnection = onlyNotConnected
                ? !customer.isAlreadyConnected || customer.connectionIsActive === false
                : true;

            return matchesSearch && matchesType && matchesConnection;
        });
        if (orderBy.orderBy) {
            data = [...data].sort((a, b) =>
                orderBy.orderDirection === OrderDirection.asc
                    ? (a.name || '').localeCompare(b.name || '')
                    : (b.name || '').localeCompare(a.name || ''),
            );
        }
        return data;
    }, [customers, search, customerType, onlyNotConnected, orderBy]);

    const setOrderOnField = (field: string) => {
        const direction = orderBy.orderDirection === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;
        setOrderBy({
            orderBy: field,
            orderDirection: direction,
        });
    };

    const handleTextFilterChange = (searchText: string) => {
        setSearch(searchText);
    };

    const handleTypeFilterChange = (event: SelectChangeEvent) => {
        const value = event.target.value as CustomerType;
        setCustomerType(value);
    };

    const Row = ({ index, style }: ListChildComponentProps) => {
        const customer = filteredData[index];
        return (
            <Box
                style={style}
                display="flex"
                alignItems="center"
                px={2}
                py={1}
                borderBottom="1px solid #e0e0e0"
                sx={{
                    '&:hover': {
                        backgroundColor: '#fafafa',
                    },
                }}
            >
                <Box flex={3} display="flex" alignItems="center" pr={2}>
                    <Typography variant="body2">{customer.name}</Typography>
                    {customer.type && (
                        <Chip size="small" label={translate(`common:${customer.type.toUpperCase()}`)} sx={{ ml: 1 }} />
                    )}
                </Box>
                <Box flex={2} pr={2}>
                    <Typography variant="body2">{customer.invoicing?.costCenter || '-'}</Typography>
                </Box>
                <Box flex={3} pr={2}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        <FormattedAddress address={customer.contact?.address} />
                    </Typography>
                </Box>
                <Box flex={2}>
                    {customer.isAlreadyConnected ? (
                        customer.connectionIsActive === false && onActivateCustomer ? (
                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                <Typography variant="body2" color="text.secondary">
                                    {translate('client-directory:INACTIVE_CUSTOMER')}
                                </Typography>
                                <Button size="small" onClick={() => onActivateCustomer(customer._id as string)}>
                                    {translate('client-directory:ACTIVATE_CUSTOMER')}
                                </Button>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {translate('client-directory:ALREADY_CONNECTED')}
                            </Typography>
                        )
                    ) : currentOrg?._id.toString() === (customer?._id || '').toString() ? (
                        <Button size="small" disabled={true}>
                            {translate('organization:YOUR_ORGANIZATION')}
                        </Button>
                    ) : (
                        <Button size="small" onClick={() => onSelectCustomer(customer._id as string, customer.type)}>
                            {addButtonTitle}
                        </Button>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Fragment>
            <Box mb={2} display="flex" flexDirection="row" gap={2} alignItems="center">
                <Box>
                    <FreeTextFilterComponent
                        value={search}
                        freeTextFilterChange={handleTextFilterChange}
                        label="search clinic freetext"
                    />
                </Box>
                {showTypeFilter && (
                    <Box>
                        <FormControl fullWidth margin="dense">
                            <InputLabel>{translate('client-directory:CUSTOMER_TYPE')}</InputLabel>
                            <Select
                                id="customer-type-select"
                                defaultValue={'ALL'}
                                label={translate('client-directory:CUSTOMER_TYPE')}
                                onChange={handleTypeFilterChange}
                                value={customerType || 'ALL'}
                            >
                                {Object.values(CustomerType).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {translate(`common:${option}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
                <Box>
                    <FormControlLabel
                        control={
                            <Switch
                                color="secondary"
                                checked={onlyNotConnected || false}
                                onChange={(_, value) => setOnlyNotConnected(value)}
                            />
                        }
                        label={translate('client-directory:SHOW_NOT_CONNECTED_ONLY')}
                    />
                </Box>
            </Box>
            <Box display="flex" fontWeight="bold" px={2} py={1} bgcolor="#f5f5f5">
                <Box flex={3}>
                    <TableSortLabel
                        active={orderBy.orderBy === 'name'}
                        direction={orderBy.orderDirection}
                        onClick={() => setOrderOnField('name')}
                    >
                        {translate('common:NAME')}
                    </TableSortLabel>
                </Box>
                <Box flex={2}>{translate('client-directory:COST_CENTER')}</Box>
                <Box flex={3}>{translate('invoices:DELIVERY_ADDRESS')}</Box>
                <Box flex={2}></Box>
            </Box>
            <List height={600} itemCount={filteredData.length} itemSize={70} width="100%">
                {Row}
            </List>
        </Fragment>
    );
};

export default CustomerList;
